import { request } from "utils/request"
import { asyncAction } from "./common"
import {
  members,
  whiteTasks,
  analyses,
  analysisDefinitions,
  assets,
  projects,
  projectTemplates,
  competenceGroups,
  teamStatus,
  samples,
  constraintChecks,
  teamInfo,
  analysisGroups,
  projectGroups,
  sampleView,
  chatBot,
  standardActions,
  limsResults,
  limsReviews,
} from "."
import { DateTime } from "luxon"
import { getState } from "storeAccess"
const initialState = {
  hasFetchedPlannedAnalyses: false,
  hasFetchedUnplannedAnalyses: false,
  hasFetchedPlannedProjectActions: false,
  hasFetchedUnplannedProjectActions: false,
  hasFetchedAnalysisDefinitions: false,
  hasFetchedAssets: false,
  hasFetchedMembers: false,
  hasFetchedWhiteTasks: false,
  hasFetchedAnalysisTasks: false,
  hasFetchedProjects: false,
  hasFetchedProjectTemplates: false,
  hasFetchedCompetenceGroups: false,
  hasFetchedTeamStatus: false,
  hasFetchedActionConstraintChecks: false,
  hasFetchedTaskConstraintChecks: false,
  hasFetchedStandardActions: false,
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "loading/fetchAll/BEGIN":
      return initialState
    /*
    case "loading/onScheduleChange/BEGIN":
      return {
        ...state,
        hasFetchedWhiteTasks: false,
        hasFetchedPlannedProjectActions: false,
        hasFetchedAnalysisTasks: false,
        hasFetchedActionConstraintChecks: false,
        hasFetchedTaskConstraintChecks: false,
      }*/
    case "analyses/fetchPlanned":
      return { ...state, hasFetchedPlannedAnalyses: true }
    case "analyses/fetchAndReplace":
      return { ...state, hasFetchedPlannedAnalyses: true }
    case "analyses/fetchUnplanned":
      return { ...state, hasFetchedUnplannedAnalyses: true }
    case "projectActions/fetchPlanned":
      return { ...state, hasFetchedPlannedProjectActions: true }
    case "projectActions/fetchUnplanned":
      return { ...state, hasFetchedUnplannedProjectActions: true }
    case "analysisDefinitions/fetch":
      return { ...state, hasFetchedAnalysisDefinitions: true }
    case "assets/fetch":
      return { ...state, hasFetchedAssets: true }
    case "members/fetch":
      return { ...state, hasFetchedMembers: true }
    case "whiteTasks/fetch":
      return { ...state, hasFetchedWhiteTasks: true }
    case "analysisTasks/fetch":
      return { ...state, hasFetchedAnalysisTasks: true }
    case "projects/fetch":
      return { ...state, hasFetchedProjects: true }
    case "projectTemplates/fetch":
      return { ...state, hasFetchedProjectTemplates: true }
    case "competenceGroups/fetch":
      return { ...state, hasFetchedCompetenceGroups: true }
    case "teamStatus/fetchCurrent":
      return { ...state, hasFetchedTeamStatus: true }
    case "constraintChecks/fetchAllAnalysisTask":
      return { ...state, hasFetchedTaskConstraintChecks: true }
    case "constraintChecks/fetchAllProjectAction":
      return { ...state, hasFetchedActionConstraintChecks: true }
    case "standardActions/fetch":
      return { ...state, hasFetchedStandardActions: true }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}
function isAnalysisReady(state, isAnalysis) {
  return (
    !isAnalysis ||
    (state.hasFetchedPlannedAnalyses &&
      state.hasFetchedUnplannedAnalyses &&
      state.hasFetchedAnalysisDefinitions &&
      state.hasFetchedTaskConstraintChecks)
  )
}

function isProjectReady(state, isProjects) {
  return !isProjects || (state.hasFetchedProjects && state.hasFetchedProjectTemplates)
}

// /librarian and /help only need teamStatus (for routing/feature flags) and members
// (for the current member's initials in LibrarianScreen). They skip the rest of
// the bootstrap so opening either in a new tab doesn't fetch all plannertech data.
const isLightweightRoute = (path) =>
  typeof path === "string" && (path.startsWith("/librarian") || path.startsWith("/help"))

export function isReady(state, fullState, currentLocation = null) {
  if (isLightweightRoute(currentLocation)) {
    return state.hasFetchedTeamStatus && state.hasFetchedMembers
  }
  const stateTmp = fullState
  let isAnalysis = stateTmp.teamStatus.showAnalyses
  let isProjects = stateTmp.teamStatus.showProjects
  return (
    isAnalysisReady(state, isAnalysis) &&
    isProjectReady(state, isProjects) &&
    state.hasFetchedAssets &&
    state.hasFetchedMembers &&
    state.hasFetchedWhiteTasks &&
    state.hasFetchedCompetenceGroups &&
    state.hasFetchedTeamStatus
  )
}

// currentLocation is used to determine whether we are in a view that doesn't need to be a part of the initial fetch
// Currently only sample-view and asset-view are not fetched on initial load
const _fetchAllImpl = asyncAction("loading/fetchAll", (dispatch, currentLocation = null) =>  {
  console.log("FETCHING EVERYTHING")
  if (isLightweightRoute(currentLocation)) {
    return Promise.all([
      dispatch(teamStatus.fetchCurrent()),
      dispatch(members.fetch()),
    ])
  }
  const state = getState()
  let isAnalysis = state.teamStatus.showAnalyses
  let isProjects = state.teamStatus.showProjects
  return Promise.all([
    request("GET", "/has-fetched"),
    dispatch(members.fetch()),
    dispatch(whiteTasks.fetch()),
    dispatch(standardActions.fetchStandardActions()),
    //isAnalysis ? dispatch(analysisTasks.fetch(true)) : "",
    dispatch(teamStatus.fetchCurrent()).then(() =>
      // Promise.all so the bootstrap awaits ALL of these — previously a comma operator
      // meant only the last dispatch was returned/awaited, leaving readiness racy.
      Promise.all(
        [
          isAnalysis ? dispatch(analyses.fetchUnplanned()) : "",
          isAnalysis ? dispatch(analyses.fetchAndReplace()) : "",
          isAnalysis ? dispatch(analyses.fetchUnassignedSamples()) : "",
          isAnalysis ? dispatch(analysisDefinitions.fetch()) : "",
          isAnalysis ? dispatch(analysisGroups.fetch()) : "",
        ].filter(Boolean)
      )
    ),
    dispatch(assets.fetch()),
    isProjects ? dispatch(projects.fetch()) : "",
    isProjects ? dispatch(projectTemplates.fetch()) : "",
    isProjects ? dispatch(projectGroups.fetch()) : "",
    dispatch(competenceGroups.fetch()),
    dispatch(samples.clear()),
    dispatch(teamStatus.fetchLastSampleAllocation()),
    dispatch(teamInfo.fetch()),
    dispatch(chatBot.refreshChat()),
    // LIMS QC results — loaded centrally so the Team Plan "awaiting review" indicator (F-C3)
    // can render without first opening each analysis. Gated on the QC module being enabled.
    state.teamStatus.showLims ? dispatch(limsResults.fetch()) : "",
    // RR3 #2: review/correction link records — loaded centrally too, so the right-panel Reviews
    // inbox is populated and review-backing projects are excluded from Unplanned Projects on ANY
    // screen that shows the panel (team-plan included), not only after first visiting Sample View.
    state.teamStatus.showLims ? dispatch(limsReviews.fetch()) : "",
    currentLocation === "/sample-view" ? dispatch(sampleView.fetch()) : "",
    currentLocation === "/asset-view" ? dispatch(assets.fetchGraphInfo()) : "",

  ]).then((_res) => {
    constraintChecks.allConstraintChecks(dispatch)
    return Promise.all([
      currentLocation !== "/sample-view" ? dispatch(sampleView.fetch()) : "",
      currentLocation !== "/asset-view" ? dispatch(assets.fetchGraphInfo()) : "",
    ])
  })
})

// In-flight dedup guard. fetchAll is dispatched from several places that overlap on a fresh
// login (src/index.jsx, the App.jsx re-bootstrap effect, the login redirect), and asyncAction
// has no dedup — so without this the whole bootstrap fires ~3x. Returning the in-flight promise
// collapses concurrent dispatches onto a single bootstrap; because `loading/fetchAll/BEGIN` then
// fires only once, the readiness-reset re-trigger loop in App.jsx also stops.
let bootstrapInFlight = null
export const fetchAll = (currentLocation = null) => (dispatch) => {
  if (bootstrapInFlight) return bootstrapInFlight
  bootstrapInFlight = Promise.resolve(dispatch(_fetchAllImpl(currentLocation))).finally(() => {
    bootstrapInFlight = null
  })
  return bootstrapInFlight
}


export const onScheduleChange = asyncAction("loading/onScheduleChange", (dispatch) => {
  const state = getState()

  // If a user moves 1 month backwards/forward we should fetch projects/planned/whiteTasks actions again
  const lastProjectFetchAfter = state.waiting.projectFetchAfter
  const lastProjectFetchBefore = state.waiting.projectFetchBefore
  const lastWhiteTaskFetchAfter = state.waiting.whiteTaskFetchAfter
  const lastWhiteTaskFetchBefore = state.waiting.whiteTaskFetchBefore
  const lastAnalysisFetchAfter = state.waiting.analysisFetchAfter;
  const lastAnalysisFetchBefore = state.waiting.analysisFetchBefore;
  const mondayDate = state.visual.scheduleMondayDate
  // The plus 2 weeks is because lastFetchBefore might be the tuesday after mondayDate
  // And the schedule can be as large as 2 weeks
  if (lastProjectFetchAfter && lastProjectFetchAfter >= DateTime.fromISO(mondayDate)) {
    dispatch(projects.fetch("past"))
  } else if (lastProjectFetchBefore && lastProjectFetchBefore <= DateTime.fromISO(mondayDate).plus({ weeks: 2 })) {
    dispatch(projects.fetch("future"))
  }
  if (lastWhiteTaskFetchAfter && lastWhiteTaskFetchAfter >= DateTime.fromISO(mondayDate)) {
    dispatch(whiteTasks.fetch("past"))
  } else if (lastWhiteTaskFetchBefore && lastWhiteTaskFetchBefore <= DateTime.fromISO(mondayDate).plus({ weeks: 2 })) {
    dispatch(whiteTasks.fetch("future"))
  }
  if (lastAnalysisFetchAfter && lastAnalysisFetchAfter >= DateTime.fromISO(mondayDate)) {
    dispatch(analyses.fetchPlanned("past"))
  } else if (lastAnalysisFetchBefore && lastAnalysisFetchBefore <= DateTime.fromISO(mondayDate).plus({ weeks: 2 })) {
    dispatch(analyses.fetchPlanned("future"))
  }

  return Promise.resolve()
})
